import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { getCached, getCachedAt, setCached } from "../utils/wsCache";

const getDefaultRange = () => {
    const today = new Date();
    const toDate = today.toISOString().split('T')[0]; // today YYYY-MM-DD
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    const fromDate = lastWeek.toISOString().split('T')[0]; // 7 days ago YYYY-MM-DD
    return { fromDate, toDate };
};

export default function usePaymentTransactions(dateRange = { fromDate: '', toDate: '' }) {
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
    const mountedRef = useRef(true);
    const retryTimeoutRef = useRef(null);

    const groupId = localStorage.getItem("group_id");
    const roleId = localStorage.getItem("role_id");

    // Compute default last 7 days if no date range provided
    const { fromDate, toDate } = dateRange.fromDate && dateRange.toDate ? dateRange : getDefaultRange();
    const cacheKey = `payment_transactions:${groupId}:${roleId}:${fromDate}:${toDate}`;

    const [transactions, setTransactions] = useState(() => getCached(cacheKey) || []);
    const [isLoading, setIsLoading] = useState(() => !getCached(cacheKey));
    const [lastUpdated, setLastUpdated] = useState(() => getCachedAt(cacheKey));

    const fetchTransactions = useCallback(async (attempt = 0, force = false) => {
        if (!mountedRef.current) return;

        // already fetched in this tab: show it without asking the server again.
        // a browser refresh clears the cache; live *_update messages keep it current
        const cached = getCached(cacheKey);
        if (cached && !force) {
            setTransactions(cached);
            setLastUpdated(getCachedAt(cacheKey));
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (!isConnected) {
                await connect();

                await new Promise((resolve, reject) => {
                    const start = Date.now();
                    const checkInterval = setInterval(() => {
                        if (!mountedRef.current) {
                            clearInterval(checkInterval);
                            reject(new Error('Component unmounted'));
                        } else if (isOpen()) {
                            clearInterval(checkInterval);
                            resolve();
                        } else if (Date.now() - start > 5000) {
                            clearInterval(checkInterval);
                            reject(new Error('Connection timeout'));
                        }
                    }, 100);
                });
            }

            const payload = {
                group_id: groupId,
                role_id: roleId,
                from_date: fromDate,
                to_date: toDate,
            };

            const response = await send('cms_get_all_payment_transactions', payload);

            if (!response || response.type !== 'cms_get_all_payment_transactions_response') {
                throw new Error('Invalid response from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('No data available. Try refreshing the page');
            }

            setCached(cacheKey, response.data);
            setTransactions(response.data);
            setLastUpdated(getCachedAt(cacheKey));
            setMessage(response.message || 'Transactions fetched successfully');
        } catch (err) {
            setError(err.message);

            if (mountedRef.current) {
                const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
                retryTimeoutRef.current = setTimeout(() => {
                    fetchTransactions(attempt + 1);
                }, delay);
            }
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [isConnected, isOpen, connect, send, groupId, roleId, fromDate, toDate, cacheKey]);

    useEffect(() => {
        mountedRef.current = true;
        fetchTransactions();

        const unregister = registerHandler('payment_transactions_update', (msg) => {
            if (Array.isArray(msg.data) && mountedRef.current) {
                setCached(cacheKey, msg.data);
                setTransactions(msg.data);
                setLastUpdated(getCachedAt(cacheKey));
            }
        });

        return () => {
            mountedRef.current = false;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            unregister();
        };
    }, [fetchTransactions, registerHandler, cacheKey]);

    return { transactions, isLoading, error, message, lastUpdated, refetch: () => fetchTransactions(0, true) };
}
